import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatNumber = (num) => {
  if (typeof num !== "number") return "0";
  return num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const CustomTooltip = ({ active, payload, unit, isStage = false }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          border: "1px solid #ccc",
          padding: "12px",
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          backdropFilter: "blur(4px)",
          maxWidth: "250px",
        }}
      >
        <p style={{ margin: "0 0 8px", fontWeight: "bold" }}>{data.name}</p>
        <p style={{ margin: "0 0 4px" }}>
          <strong>Water Required:</strong> {formatNumber(data.value)} {unit}
        </p>
        {data.percentage && (
          <p style={{ margin: "0 0 4px" }}>
            <strong>Percentage:</strong> {data.percentage.toFixed(1)}%
          </p>
        )}
        {isStage && data.crops && data.crops.length > 0 && (
          <div style={{ marginTop: "8px" }}>
            <p
              style={{
                margin: "0 0 4px",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              Contributing Crops:
            </p>
            {data.crops.map((crop, index) => (
              <p key={index} style={{ margin: "0", fontSize: "11px" }}>
                • {crop.name}: {formatNumber(crop.water)} {unit} ({crop.area}{" "}
                acres)
              </p>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
};

const STAGE_COLORS = {
  initial: "rgba(76, 175, 80, 0.65)",
  development: "rgba(33, 150, 243, 0.65)",
  mid_season: "rgba(255, 152, 0, 0.65)",
  late_season: "rgba(244, 67, 54, 0.65)",
};

const hslToRgba = (h, s, l, a = 0.6) => {
  const hDecimal = h / 360;
  const sDecimal = s / 100;
  const lDecimal = l / 100;

  const c = (1 - Math.abs(2 * lDecimal - 1)) * sDecimal;
  const x = c * (1 - Math.abs(((hDecimal * 6) % 2) - 1));
  const m = lDecimal - c / 2;

  let r, g, b;

  if (hDecimal < 1 / 6) {
    r = c;
    g = x;
    b = 0;
  } else if (hDecimal < 2 / 6) {
    r = x;
    g = c;
    b = 0;
  } else if (hDecimal < 3 / 6) {
    r = 0;
    g = c;
    b = x;
  } else if (hDecimal < 4 / 6) {
    r = 0;
    g = x;
    b = c;
  } else if (hDecimal < 5 / 6) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const ThreeChartWaterDisplay = ({ combinedData = null }) => {
  const [monthlyDataLiters, setMonthlyDataLiters] = useState([]);
  const [stageDataCubicMeters, setStageDataCubicMeters] = useState([]);
  const [cropDataCubicMeters, setCropDataCubicMeters] = useState([]);
  const [totalWaterLiters, setTotalWaterLiters] = useState(0);
  const [totalWaterCubicMeters, setTotalWaterCubicMeters] = useState(0);
  const [totalArea, setTotalArea] = useState(0);
  const [cropList, setCropList] = useState([]);

  const calculateCropMonthlyDistribution = (stagesData, plantingDateStr) => {
    const monthlyWater = Array(12).fill(0);

    if (!stagesData || !plantingDateStr) {
      return monthlyWater;
    }

    try {
      const plantingDate = new Date(plantingDateStr);
      let totalDays = 0;
      let totalWaterLiters = 0;

      Object.entries(stagesData).forEach(([stage, data]) => {
        if (data) {
          totalDays += data.days || 0;
          totalWaterLiters += data.total_water_requirement_liters || 0;
        }
      });

      if (totalDays === 0 || totalWaterLiters === 0) {
        return monthlyWater;
      }

      const waterPerDay = totalWaterLiters / totalDays;
      let currentDate = new Date(plantingDate);

      for (let day = 0; day < totalDays; day++) {
        const month = currentDate.getMonth();
        monthlyWater[month] += waterPerDay;
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } catch (error) {
      console.error("Error calculating crop monthly distribution:", error);
    }

    return monthlyWater;
  };

  useEffect(() => {
    if (
      !combinedData ||
      !combinedData.crops ||
      combinedData.crops.length === 0
    ) {

      setMonthlyDataLiters(
        MONTHS.map((month, index) => ({ name: month, value: 0, month: index }))
      );
      setStageDataCubicMeters([]);
      setCropDataCubicMeters([]);
      setTotalWaterLiters(0);
      setTotalWaterCubicMeters(0);
      setTotalArea(0);
      setCropList([]);
      return;
    }

    try {
      const allCrops = combinedData.crops;
      let totalLiters = 0;
      let totalCubicMeters = 0;
      let totalAcres = 0;

      const monthlyStackedData = MONTHS.map((month, index) => ({
        name: month,
        month: index,
        total: 0,
      }));

      const cropsList = [];
      const cropPieData = [];

      const stageAccumulator = {
        initial: { water: 0, crops: [] },
        development: { water: 0, crops: [] },
        mid_season: { water: 0, crops: [] },
        late_season: { water: 0, crops: [] },
      };

      allCrops.forEach((cropResult, index) => {
        if (
          !cropResult.growth_stage_calculations ||
          !cropResult.growth_stage_calculations.growth_stages
        ) {
          return;
        }

        const cropName =
          cropResult.crop_input.crop_type.charAt(0).toUpperCase() +
          cropResult.crop_input.crop_type.slice(1);
        const cropArea = parseFloat(cropResult.crop_input.area) || 0;

        const hue = (index * 137.5) % 360;
        const cropColor = hslToRgba(hue, 70, 50, 0.65);

        totalAcres += cropArea;

        cropsList.push({
          name: cropName,
          key: cropName.toLowerCase().replace(/\s+/g, "_"),
          color: cropColor,
          area: cropArea,
        });

        let cropTotalLiters = 0;
        let cropTotalCubicMeters = 0;

        const cropMonthlyWater = calculateCropMonthlyDistribution(
          cropResult.growth_stage_calculations.growth_stages,
          cropResult.crop_input.planting_date
        );

        cropMonthlyWater.forEach((monthWater, monthIndex) => {
          monthlyStackedData[monthIndex][
            cropName.toLowerCase().replace(/\s+/g, "_")
          ] = monthWater;
          monthlyStackedData[monthIndex].total += monthWater;
        });

        Object.entries(
          cropResult.growth_stage_calculations.growth_stages
        ).forEach(([stageName, stage]) => {
          if (stage && stageAccumulator[stageName]) {
            const stageLiters = stage.total_water_requirement_liters || 0;
            const stageCubicMeters =
              stage.total_water_requirement_cubic_meters || stageLiters / 1000;

            stageAccumulator[stageName].water += stageCubicMeters;
            stageAccumulator[stageName].crops.push({
              name: cropName,
              water: stageCubicMeters,
              area: cropArea,
            });

            totalLiters += stageLiters;
            totalCubicMeters += stageCubicMeters;

            cropTotalLiters += stageLiters;
            cropTotalCubicMeters += stageCubicMeters;
          }
        });

        cropPieData.push({
          name: cropName,
          value: cropTotalCubicMeters,
          valueLiters: cropTotalLiters,
          area: cropArea,
          fill: cropColor,
        });
      });

      const stageData = Object.entries(stageAccumulator)
        .map(([stageName, data]) => {
          const displayName = stageName
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          return {
            name: displayName,
            value: data.water,
            percentage:
              totalCubicMeters > 0 ? (data.water / totalCubicMeters) * 100 : 0,
            crops: data.crops,
            fill: STAGE_COLORS[stageName] || "rgba(153, 153, 153, 0.65)",
          };
        })
        .filter((stage) => stage.value > 0);

      cropPieData.forEach((crop) => {
        crop.percentage =
          totalCubicMeters > 0 ? (crop.value / totalCubicMeters) * 100 : 0;
      });

      setMonthlyDataLiters(monthlyStackedData);
      setStageDataCubicMeters(stageData);
      setCropDataCubicMeters(cropPieData);
      setTotalWaterLiters(totalLiters);
      setTotalWaterCubicMeters(totalCubicMeters);
      setTotalArea(totalAcres);
      setCropList(cropsList);
    } catch (error) {
      console.error("Error processing water requirements data:", error);
    }
  }, [combinedData]);

  if (!combinedData || !combinedData.crops || combinedData.crops.length === 0) {
    return (
      <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
        <h3>Multi-Crop Water Analysis</h3>
        <p>
          No crop data available. Please submit crops to see water requirements.
        </p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      {}
      <div
        style={{
          textAlign: "center",
          marginBottom: "var(--space-6)",
          paddingBottom: "var(--space-4)",
          borderBottom: `2px solid var(--color-primary-500)`,
        }}
      >
        <h3
          style={{
            margin: "0",
            fontSize: "var(--font-size-xl)",
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--color-neutral-900)",
            lineHeight: "var(--line-height-tight)",
          }}
        >
          💧 Water Requirements Analysis
        </h3>
      </div>

      {}
      <div
        style={{
          marginBottom: "var(--space-6)",
          padding: "var(--space-5)",
          backgroundColor: "var(--color-primary-50)",
          borderRadius: "var(--radius-lg)",
          border: `1px solid var(--color-primary-200)`,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "var(--space-4)",
            textAlign: "center",
          }}
        >
          <div>
            <p style={{
              margin: "0",
              fontSize: "var(--font-size-sm)",
              color: "var(--color-neutral-600)",
              fontWeight: "var(--font-weight-medium)"
            }}>
              🌾 Total Crops
            </p>
            <p
              style={{
                margin: "0",
                fontSize: "var(--font-size-2xl)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-primary-700)",
              }}
            >
              {combinedData.total_crops}
            </p>
          </div>
          <div>
            <p style={{
              margin: "0",
              fontSize: "var(--font-size-sm)",
              color: "var(--color-neutral-600)",
              fontWeight: "var(--font-weight-medium)"
            }}>
              📏 Total Area
            </p>
            <p
              style={{
                margin: "0",
                fontSize: "var(--font-size-2xl)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-secondary-700)",
              }}
            >
              {totalArea} acres
            </p>
          </div>
          <div>
            <p style={{
              margin: "0",
              fontSize: "var(--font-size-sm)",
              color: "var(--color-neutral-600)",
              fontWeight: "var(--font-weight-medium)"
            }}>
              💧 Total Water (m³)
            </p>
            <p
              style={{
                margin: "0",
                fontSize: "var(--font-size-2xl)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-primary-700)",
              }}
            >
              {formatNumber(totalWaterCubicMeters)}
            </p>
          </div>
          <div>
            <p style={{
              margin: "0",
              fontSize: "var(--font-size-sm)",
              color: "var(--color-neutral-600)",
              fontWeight: "var(--font-weight-medium)"
            }}>
              🚰 Total Water (L)
            </p>
            <p
              style={{
                margin: "0",
                fontSize: "var(--font-size-2xl)",
                fontWeight: "var(--font-weight-bold)",
                color: "var(--color-primary-700)",
              }}
            >
              {formatNumber(totalWaterLiters)}
            </p>
          </div>
        </div>
      </div>
      {}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "25px",
          width: "100%",
        }}
      >
        {}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
          }}
        >
          {}
          <div
            style={{
              border: `var(--card-border-width) solid var(--card-border-color)`,
              borderRadius: "var(--radius-lg)",
              padding: "var(--space-5)",
              backgroundColor: "#ffffff",
              boxShadow: "var(--shadow-sm)",
              transition: "all var(--transition-base)",
            }}
          >
            <h4
              style={{
                textAlign: "center",
                margin: "0 0 var(--space-4)",
                fontSize: "var(--font-size-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-neutral-900)",
                lineHeight: "var(--line-height-tight)",
              }}
            >
              💧 Water by Growth Stages
            </h4>
            <div style={{ height: "350px" }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={stageDataCubicMeters}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={110}
                    dataKey="value"
                    nameKey="name"
                    stroke="rgba(255, 255, 255, 0.8)"
                    strokeWidth={2}
                    label={({ name, percent }) =>
                      `${(percent * 100).toFixed(1)}%`
                    }
                  >
                    {stageDataCubicMeters.map((entry, index) => (
                      <Cell key={`stage-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={(props) => (
                      <CustomTooltip {...props} unit="m³" isStage={true} />
                    )}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {}
          <div
            style={{
              border: `var(--card-border-width) solid var(--card-border-color)`,
              borderRadius: "var(--radius-lg)",
              padding: "var(--space-5)",
              backgroundColor: "#ffffff",
              boxShadow: "var(--shadow-sm)",
              transition: "all var(--transition-base)",
            }}
          >
            <h4
              style={{
                textAlign: "center",
                margin: "0 0 var(--space-4)",
                fontSize: "var(--font-size-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-neutral-900)",
                lineHeight: "var(--line-height-tight)",
              }}
            >
              🌾 Water by Crop Type
            </h4>
            <div style={{ height: "350px" }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={cropDataCubicMeters}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={110}
                    dataKey="value"
                    nameKey="name"
                    stroke="rgba(255, 255, 255, 0.8)"
                    strokeWidth={2}
                    label={({ name, percent }) =>
                      `${(percent * 100).toFixed(1)}%`
                    }
                  >
                    {cropDataCubicMeters.map((entry, index) => (
                      <Cell key={`crop-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={(props) => (
                      <CustomTooltip {...props} unit="m³" isStage={false} />
                    )}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {}
        <div
          style={{
            border: "2px solid #e0e0e0",
            borderRadius: "12px",
            padding: "20px",
            backgroundColor: "rgba(250, 250, 250, 0.8)",
            backdropFilter: "blur(2px)",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              textAlign: "center",
              marginBottom: "20px",
              color: "#333",
            }}
          >
            📅 Monthly Water Schedule by Crop (Liters)
          </h4>

          <div style={{ height: "450px" }}>
            <ResponsiveContainer>
              <BarChart
                data={monthlyDataLiters}
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(221, 221, 221, 0.3)"
                />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12, fill: "#666" }}
                  axisLine={{ stroke: "rgba(0, 0, 0, 0.2)" }}
                  tickLine={{ stroke: "rgba(0, 0, 0, 0.2)" }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#666" }}
                  label={{
                    value: "Water Required (Liters)",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle", fontSize: 14, fill: "#333" },
                  }}
                  axisLine={{ stroke: "rgba(0, 0, 0, 0.2)" }}
                  tickLine={{ stroke: "rgba(0, 0, 0, 0.2)" }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const totalWater = payload.reduce(
                        (sum, entry) => sum + (entry.value || 0),
                        0
                      );
                      return (
                        <div
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "2px solid #ccc",
                            padding: "15px",
                            borderRadius: "8px",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                            backdropFilter: "blur(4px)",
                            maxWidth: "320px",
                          }}
                        >
                          <p
                            style={{
                              margin: "0 0 10px",
                              fontWeight: "bold",
                              fontSize: "14px",
                            }}
                          >
                            📅 {label}
                          </p>
                          <p style={{ margin: "0 0 10px", fontSize: "13px" }}>
                            <strong>
                              🚰 Total: {formatNumber(totalWater)} liters
                            </strong>
                          </p>
                          {payload.map((entry, index) => (
                            <p
                              key={index}
                              style={{
                                margin: "0",
                                fontSize: "12px",
                                color: entry.color,
                              }}
                            >
                              🌱 {entry.name}: {formatNumber(entry.value || 0)}{" "}
                              liters
                            </p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                {cropList.map((crop) => (
                  <Bar
                    key={crop.key}
                    dataKey={crop.key}
                    stackId="crops"
                    name={crop.name}
                    fill={crop.color}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreeChartWaterDisplay;
